import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/ApiService';
import Navigation from '../components/Navigation';
import Avatar from '../components/Avatar';
import UserProfileModal from '../components/UserProfileModal';
import './Browse.css';

interface User {
  id: number;
  name?: string;
  email: string;
  distance?: number | null;
  profile?: {
    bio?: string;
    age?: number;
    location?: string;
    interests?: string;
    photoUrl?: string;
    photos?: Array<{ id: number; url: string; order: number }>;
    variant?: {
      name: string;
      description?: string;
    };
  };
  mood?: {
    mood: string;
    expiresAt: string;
  } | null;
  greenFlags?: Array<{
    flag: {
      id: number;
      name: string;
      emoji?: string;
      description: string;
      category: string;
    };
    verifiedBy?: number;
  }>;
  hashtags?: Array<{
    hashtag: {
      id: number;
      name: string;
      displayName: string;
      category: string | null;
      isOfficial: boolean;
      userCount: number;
    };
  }>;
}

const Browse: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await ApiService.get('/users/browse');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    console.log('=== LIKE BUTTON CLICKED ===');
    console.log('selectedUser:', selectedUser?.id);
    console.log('actionLoading:', actionLoading);

    if (!selectedUser) {
      console.log('No selected user');
      return;
    }

    if (actionLoading) {
      console.log('Already loading, ignoring click');
      return;
    }

    setMessage('');
    setActionLoading(true);
    console.log('Making API call to like user:', selectedUser.id);

    try {
      const response = await ApiService.post(`/likes/${selectedUser.id}`);
      console.log('Like API response:', response.data);

      if (response.data.match) {
        setMessage(`🎉 It's a match with ${selectedUser.name || 'this user'}!`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`✅ Liked ${selectedUser.name || 'this user'}!`);
        setTimeout(() => setMessage(''), 2000);
      }

      // Remove liked user from the list
      setUsers(users.filter(u => u.id !== selectedUser.id));
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Failed to like user:', error);
      console.error('Error response:', error.response?.data);

      let errorMsg = 'Failed to like user. Please try again.';

      if (error.response?.data) {
        const data = error.response.data;

        // Check for quota limit error
        if (data.error === 'Daily like limit reached' || data.upgradeRequired) {
          errorMsg = `Daily like limit reached (${data.used}/${data.limit}). Upgrade to get more likes!`;
        } else if (data.message) {
          errorMsg = data.message;
        } else if (data.error) {
          errorMsg = data.error;
        }
      }

      setMessage(`❌ ${errorMsg}`);
      setTimeout(() => setMessage(''), 8000);
    } finally {
      setActionLoading(false);
      console.log('Like action completed, actionLoading set to false');
    }
  };

  const handlePass = () => {
    if (!selectedUser) return;
    // Remove passed user from the list
    setUsers(users.filter(u => u.id !== selectedUser.id));
    setSelectedUser(null);
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="browse-container">
          <p>Loading...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="browse-container">
        <div style={{ textAlign: 'center', padding: '20px', background: 'yellow', fontWeight: 'bold', fontSize: '24px', margin: '20px 0' }}>
          🔥 VERSION 99.99 - TEST BUILD 🔥
        </div>
        <h1>Discover Connections</h1>

        {message && <div className="match-message">{message}</div>}

        {users.length > 0 ? (
          <div className="users-grid">
            {users.map((user) => (
              <div
                key={user.id}
                className="user-card-preview"
                onClick={() => setSelectedUser(user)}
              >
                <div className="card-photo">
                  <Avatar
                    photoUrl={user.profile?.photoUrl}
                    name={user.name}
                    size="large"
                  />
                </div>

                <div className="card-info">
                  <h3>
                    {user.name || 'Anonymous'}
                    {user.profile?.age && <span className="age">, {user.profile.age}</span>}
                  </h3>

                  {user.profile?.variant && (
                    <div className="variant-badge-small">
                      {user.profile.variant.name}
                    </div>
                  )}

                  {user.profile?.location && (
                    <p className="location-small">
                      📍 {user.profile.location}
                      {user.distance && ` • ${user.distance}mi`}
                    </p>
                  )}

                  {user.greenFlags && user.greenFlags.length > 0 && (
                    <div className="flags-preview">
                      {user.greenFlags.slice(0, 3).map((userFlag) => (
                        <span key={userFlag.flag.id} className="flag-emoji">
                          {userFlag.flag.emoji}
                        </span>
                      ))}
                      {user.greenFlags.length > 3 && (
                        <span className="flag-more">+{user.greenFlags.length - 3}</span>
                      )}
                    </div>
                  )}

                  {user.hashtags && user.hashtags.length > 0 && (
                    <div className="hashtags-preview">
                      {user.hashtags.slice(0, 3).map((userHashtag) => (
                        <button
                          key={userHashtag.hashtag.id}
                          className="hashtag-mini"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/hashtags/${userHashtag.hashtag.id}`);
                          }}
                        >
                          {userHashtag.hashtag.displayName}
                        </button>
                      ))}
                      {user.hashtags.length > 3 && (
                        <span className="hashtag-more">+{user.hashtags.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-users">
            <p>No more users to browse right now.</p>
            <button onClick={fetchUsers} className="refresh-button">
              Refresh
            </button>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserProfileModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onLike={handleLike}
          onPass={handlePass}
          actionLoading={actionLoading}
        />
      )}
    </>
  );
};

export default Browse;
